/**
 * One run of an agent, as an organization's trail of runs keeps it — the shape
 * an install sends (`POST /api/cloud/orgs/:org/runs`) and the account area
 * stores and reads back (`org_runs`).
 *
 * A run is one session: what a task started, from its first boot to the moment
 * it was closed, refused or failed. Its record says **who ran what, with which
 * agent and model, for how long, at what cost, and what came of it** — and
 * nothing that was typed, written or read along the way. There is no field for
 * a prompt, a reply, a file, a diff, a command line or a secret, and the parser
 * refuses a key it does not know rather than dropping it: a collector that tries
 * to send a value finds out in a test, not in a trail every administrator reads
 * (the rule `org_audit.detail` keeps, `auditDetail` in the Worker).
 *
 * What it holds instead are counts, names the app chose (agent, model, effort,
 * outcome), domain names and pull request addresses. A domain is not a secret:
 * it is what the sandbox's egress policy was asked about, and a refused one is
 * exactly what an administrator reading the trail is looking for. A pull request
 * address is where the work went, which the organization's own Git host already
 * shows to everybody in it.
 *
 * **The record is replaced, never added to.** An install sends a run whole each
 * time it changes — a turn ended, a host was refused, the session closed — and
 * the account area keeps the last copy under the run's id, so a retry after a
 * lost answer, or the same run sent twice, changes nothing.
 */

import type { BudgetSignal } from './spend';

/** What a run came to. `running` until the session is closed, refused or failed. */
export const RUN_OUTCOMES = ['running', 'landed', 'pr-opened', 'abandoned', 'failed'] as const;
export type RunOutcome = (typeof RUN_OUTCOMES)[number];

/** Words for each outcome, as both pages say them. */
export const RUN_OUTCOME_WORDS: Record<RunOutcome, string> = {
  running: 'Running',
  landed: 'Landed',
  'pr-opened': 'Pull request',
  abandoned: 'Abandoned',
  failed: 'Failed'
};

export interface RunTokens {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

export interface RunRecord {
  /** The install's own id for the run — its session id. Unique per member; the key a copy replaces. */
  id: string;
  /** The organization's workspace the run was in (its `org_projects` id), never a folder. */
  workspaceId: string;
  /** The task the run was started for, where a task started it. */
  taskId: string | null;
  /** `claude-code`, `codex` — the agent the session launched with. */
  agent: string;
  /** The model and effort the session was started on, as the app names them. */
  model: string;
  effort: string;
  startedAt: string;
  /** When the session was closed, refused or failed; null while it runs. */
  endedAt: string | null;
  outcome: RunOutcome;
  /** Turns the agent handed back, and tools it called on the way. */
  turns: number;
  toolCalls: number;
  /** What its merges landed on the host, summed over every merge. */
  filesChanged: number;
  linesAdded: number;
  linesRemoved: number;
  merges: number;
  /** The pull requests it opened, by address. */
  pullRequests: string[];
  /** Domains the sandbox was refused, and those a person allowed when asked. */
  refusedHosts: string[];
  allowedHosts: string[];
  tokens: RunTokens;
  /** An estimate at API list prices, in micro-dollars (`@airlock/contracts/usage/prices`). */
  costMicros: number;
  /**
   * The team's Rules on this run (§5.27), once it was audited: how many audits
   * a person asked for, what the last one answered, and the ids of the rules it
   * found broken — names the organization chose (`acme:3/no-literals`), never a
   * finding's words, a file or a line. Absent from a run never audited, and from
   * an install that predates it.
   */
  rules?: RunRules;
}

export const RUN_RULES_VERDICTS = ['clean', 'should', 'must', 'failed'] as const;
export type RunRulesVerdict = (typeof RUN_RULES_VERDICTS)[number];

export interface RunRules {
  audits: number;
  verdict: RunRulesVerdict;
  /** The rules the last audit found broken, each once, at most `MAX_RUN_RULES` — the `blocking` ones first. */
  violated: string[];
  /** How many of those were enforced MUSTs — what held the merge. */
  blocking: number;
}

/** How many broken rules a record names. */
export const MAX_RUN_RULES = 50;
/** A rule's id: its source (the organization's slug, or `ws`), its RFC's number and its slug. */
const RULE_ID = /^[a-z0-9][a-z0-9-]{0,62}:\d{1,5}\/[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** How many runs one upload may carry — a backlog larger than this goes in several. */
export const MAX_UPLOAD_RUNS = 100;
/** How many hosts of each kind a record keeps, and pull requests. */
export const MAX_RUN_HOSTS = 100;
export const MAX_RUN_PULLS = 20;

/** `POST /api/cloud/orgs/:org/runs`. */
export interface RunUpload {
  runs: RunRecord[];
}

export interface RunUploadAnswer {
  stored: number;
  /**
   * The highest budget signal this member's runs fall under this month, or
   * null (`@airlock/contracts/runs/spend`). Absent from an account area that
   * predates budgets.
   */
  budget?: BudgetSignal | null;
}

/** A run as the page reads it: the record, and who sent it. */
export interface RunRow extends RunRecord {
  member: { email: string; name: string | null };
  workspaceName: string | null;
  updatedAt: string;
}

/** `GET /api/orgs/:org/runs`. */
export interface OrgRunsAnswer {
  runs: RunRow[];
  /** How many runs the filters match in all, every page counted. */
  total: number;
  /** Where the next page starts, or null at the end. */
  cursor: string | null;
  /** Whether the caller sees every member (owner, admin) or only themselves. */
  everyone: boolean;
  /** Whether the organization records runs (`runTrailEnabled` published on), and whether it holds that answer. */
  collects: boolean;
  held: boolean;
}

/* ── the parser ───────────────────────────────────────────────────────────── */

const ID = /^[A-Za-z0-9_-]{1,80}$/;
const AGENT = /^[a-z0-9-]{1,40}$/;
const MODEL = /^[A-Za-z0-9._:[\]-]{1,80}$/;
const EFFORT = /^[a-z]{0,20}$/;
const MAX_COUNT = 1_000_000_000_000;
const LABEL = '[a-z0-9_](?:[a-z0-9_-]{0,61}[a-z0-9_])?';
const HOST = new RegExp(`^(?=.{1,253}$)${LABEL}(?:\\.${LABEL})*$`);
const KEYS: readonly (keyof RunRecord)[] = [
  'id',
  'workspaceId',
  'taskId',
  'agent',
  'model',
  'effort',
  'startedAt',
  'endedAt',
  'outcome',
  'turns',
  'toolCalls',
  'filesChanged',
  'linesAdded',
  'linesRemoved',
  'merges',
  'pullRequests',
  'refusedHosts',
  'allowedHosts',
  'tokens',
  'costMicros',
  'rules'
];
const TOKEN_KEYS: readonly (keyof RunTokens)[] = ['input', 'output', 'cacheRead', 'cacheWrite'];

/** A domain name as a record keeps it: lower case, no port, no trailing dot. */
export function isRunHost(value: unknown): value is string {
  return typeof value === 'string' && HOST.test(value);
}

/**
 * A pull request's address as a record keeps it: `https`, a host, a path — and
 * no credentials, query or fragment, which is where a token would hide.
 */
export function isPullAddress(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 300) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash && url.pathname.length > 1 && isRunHost(url.hostname);
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_COUNT;
}

function isInstant(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 40 && !Number.isNaN(Date.parse(value));
}

/**
 * One record as it may be stored, or every reason it may not. Unknown keys are
 * refused, not dropped: the shape is the promise that no value travels.
 */
export function parseRunRecord(input: unknown): { ok: true; run: RunRecord } | { ok: false; problems: string[] } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return { ok: false, problems: ['Expected an object.'] };
  const raw = input as Record<string, unknown>;
  const problems: string[] = [];
  for (const key of Object.keys(raw)) {
    if (!(KEYS as readonly string[]).includes(key)) problems.push(`${key}: a run holds counts, names and addresses — not this.`);
  }
  const need = (ok: boolean, key: string, what: string): void => {
    if (!ok) problems.push(`${key}: expected ${what}.`);
  };
  need(typeof raw.id === 'string' && ID.test(raw.id), 'id', 'an id');
  need(typeof raw.workspaceId === 'string' && ID.test(raw.workspaceId), 'workspaceId', 'an id');
  need(raw.taskId === null || (typeof raw.taskId === 'string' && ID.test(raw.taskId)), 'taskId', 'an id or null');
  need(typeof raw.agent === 'string' && AGENT.test(raw.agent), 'agent', 'an agent id');
  need(typeof raw.model === 'string' && MODEL.test(raw.model), 'model', 'a model id');
  need(typeof raw.effort === 'string' && EFFORT.test(raw.effort), 'effort', 'an effort');
  need(isInstant(raw.startedAt), 'startedAt', 'a date');
  need(raw.endedAt === null || isInstant(raw.endedAt), 'endedAt', 'a date or null');
  need((RUN_OUTCOMES as readonly unknown[]).includes(raw.outcome), 'outcome', RUN_OUTCOMES.join(', '));
  for (const key of ['turns', 'toolCalls', 'filesChanged', 'linesAdded', 'linesRemoved', 'merges', 'costMicros'] as const) {
    need(isCount(raw[key]), key, 'a whole number');
  }
  const list = (key: 'pullRequests' | 'refusedHosts' | 'allowedHosts', max: number, check: (value: unknown) => boolean): string[] => {
    const value = raw[key];
    if (!Array.isArray(value) || value.length > max || !value.every(check)) {
      problems.push(`${key}: expected at most ${max} ${key === 'pullRequests' ? 'https addresses' : 'domain names'}.`);
      return [];
    }
    return [...new Set(value as string[])];
  };
  const pullRequests = list('pullRequests', MAX_RUN_PULLS, isPullAddress);
  const refusedHosts = list('refusedHosts', MAX_RUN_HOSTS, isRunHost);
  const allowedHosts = list('allowedHosts', MAX_RUN_HOSTS, isRunHost);
  const tokens = raw.tokens;
  const tokensOk =
    typeof tokens === 'object' &&
    tokens !== null &&
    !Array.isArray(tokens) &&
    Object.keys(tokens).every((key) => (TOKEN_KEYS as readonly string[]).includes(key)) &&
    TOKEN_KEYS.every((key) => isCount((tokens as Record<string, unknown>)[key]));
  need(tokensOk, 'tokens', 'input, output, cacheRead and cacheWrite as whole numbers');
  const rules = raw.rules === undefined ? undefined : runRulesOf(raw.rules, problems);
  if (problems.length) return { ok: false, problems };
  const t = tokens as RunTokens;
  return {
    ok: true,
    run: {
      id: raw.id as string,
      workspaceId: raw.workspaceId as string,
      taskId: raw.taskId as string | null,
      agent: raw.agent as string,
      model: raw.model as string,
      effort: raw.effort as string,
      startedAt: new Date(raw.startedAt as string).toISOString(),
      endedAt: raw.endedAt === null ? null : new Date(raw.endedAt as string).toISOString(),
      outcome: raw.outcome as RunOutcome,
      turns: raw.turns as number,
      toolCalls: raw.toolCalls as number,
      filesChanged: raw.filesChanged as number,
      linesAdded: raw.linesAdded as number,
      linesRemoved: raw.linesRemoved as number,
      merges: raw.merges as number,
      pullRequests,
      refusedHosts,
      allowedHosts,
      tokens: { input: t.input, output: t.output, cacheRead: t.cacheRead, cacheWrite: t.cacheWrite },
      costMicros: raw.costMicros as number,
      ...(rules ? { rules } : {})
    }
  };
}

/** A run's Rules summary as it may be stored, or a sentence in `problems`. */
function runRulesOf(input: unknown, problems: string[]): RunRules | undefined {
  const raw = input as Record<string, unknown> | null;
  const ok =
    typeof raw === 'object' &&
    raw !== null &&
    !Array.isArray(raw) &&
    Object.keys(raw).every((key) => ['audits', 'verdict', 'violated', 'blocking'].includes(key)) &&
    isCount(raw.audits) &&
    (raw.audits as number) > 0 &&
    (RUN_RULES_VERDICTS as readonly unknown[]).includes(raw.verdict) &&
    Array.isArray(raw.violated) &&
    raw.violated.length <= MAX_RUN_RULES &&
    raw.violated.every((one) => typeof one === 'string' && RULE_ID.test(one)) &&
    isCount(raw.blocking) &&
    (raw.blocking as number) <= raw.violated.length;
  if (!ok) {
    problems.push(`rules: expected audits, a verdict (${RUN_RULES_VERDICTS.join(', ')}), at most ${MAX_RUN_RULES} rule ids and how many were blocking.`);
    return undefined;
  }
  return {
    audits: raw.audits as number,
    verdict: raw.verdict as RunRulesVerdict,
    violated: [...new Set(raw.violated as string[])],
    blocking: raw.blocking as number
  };
}

/** The total tokens of a run — what the spend pages add up beside the cost. */
export function totalTokens(tokens: RunTokens): number {
  return tokens.input + tokens.output + tokens.cacheRead + tokens.cacheWrite;
}
